import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Users, Calendar, TrendingUp, Building, Phone, Mail, Edit, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const AgentProfile = ({ agent, onClose, BACKEND_URL, API }) => {
  const [agentStats, setAgentStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (agent && agent.id) {
      fetchAgentStats();
    }
  }, [agent]);

  const fetchAgentStats = async () => {
    try {
      const response = await axios.get(`${API}/agents/${agent.id}/stats`);
      setAgentStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching agent stats:", error);
      // Set default stats if API fails
      setAgentStats({
        total_visits: 0,
        managed_properties: 0,
        sold_properties: 0,
        success_rate: 0,
        completed_visits: 0,
        pending_visits: 0,
        rented_properties: 0,
        total_sales_value: 0,
        commission_earned: 0
      });
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-SN', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(price || 0);
  };

  if (!agent) {
    return <div className="p-6">Agent non trouvé</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onClose}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux Agents
        </Button>
      </div>

      {/* Profile Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {agent.prenom[0]}{agent.nom[0]}
            </div>
            <div className="flex-1">
              <CardTitle className="text-2xl">{agent.prenom} {agent.nom}</CardTitle>
              <p className="text-gray-600">{agent.specialite || 'Agent immobilier'}</p>
              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                <div className="flex items-center">
                  <Phone className="h-4 w-4 mr-1" />
                  {agent.telephone}
                </div>
                <div className="flex items-center">
                  <Mail className="h-4 w-4 mr-1" />
                  {agent.email}
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Stats Overview */}
      {loading ? (
        <div className="text-center py-8">Chargement des statistiques...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Visites totales</p>
                  <p className="text-2xl font-bold">{agentStats?.total_visits || 0}</p>
                </div>
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Calendar className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Biens gérés</p>
                  <p className="text-2xl font-bold">{agentStats?.managed_properties || 0}</p>
                </div>
                <div className="p-2 bg-green-50 rounded-lg">
                  <Building className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Ventes réalisées</p>
                  <p className="text-2xl font-bold">{agentStats?.sold_properties || 0}</p>
                </div>
                <div className="p-2 bg-purple-50 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Taux de réussite</p>
                  <p className="text-2xl font-bold">{agentStats?.success_rate?.toFixed(1) || 0}%</p>
                </div>
                <div className="p-2 bg-orange-50 rounded-lg">
                  <Users className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Performance Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Performance des visites</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Visites effectuées</span>
              <Badge className="bg-green-100 text-green-800">
                {agentStats?.completed_visits || 0}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Visites programmées</span>
              <Badge className="bg-blue-100 text-blue-800">
                {agentStats?.pending_visits || 0}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Locations conclues</span>
              <Badge className="bg-indigo-100 text-indigo-800">
                {agentStats?.rented_properties || 0}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Chiffre d'affaires</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">Valeur totale des ventes</p>
              <p className="text-xl font-bold text-green-600">
                {formatPrice(agentStats?.total_sales_value || 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Commissions générées</p>
              <p className="text-xl font-bold text-blue-600">
                {formatPrice(agentStats?.commission_earned || 0)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AgentProfile;